import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { Button, Tag } from "../../../../design-system";
import {
  disconnectZoom,
  getZoomConnection,
  startZoomAuth,
  ZoomConnection,
} from "../../../../database/booking/zoomApi";

const Panel = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;
const Copy = styled.div`flex: 1 1 320px;`;
const Title = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;
const Detail = styled.div`
  margin-top: var(--space-1);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;
const ErrorText = styled.div`
  flex-basis: 100%;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-danger);
`;

export const ZoomConnectPanel: React.FC<{ communityId?: string }> = ({ communityId }) => {
  const [connection, setConnection] = useState<ZoomConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      setConnection(await getZoomConnection(communityId));
    } catch (err: any) {
      setError(err?.message || "Could not load Zoom status.");
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  // Connecting here replaces a shared alias with this community's own tokens.
  // Zoom keeps one refresh token per user and app grant and rotates it on every
  // refresh, so two docs holding the same account invalidate each other. Taking
  // over stays allowed, but the operator has to say yes to it first.
  const confirmTakeover = (sharedFrom: string) =>
    window.confirm(
      `This community hosts meetings on ${sharedFrom}'s Zoom account.\n\n` +
        "Connecting a separate account here stops the sharing. If you connect " +
        `the same Zoom account again, ${sharedFrom}'s meeting links can break. ` +
        "Zoom allows one live connection per account.\n\nContinue?"
    );

  const connect = async () => {
    if (!communityId) return;
    const owner = connection?.sharedFrom || "";
    if (owner && !confirmTakeover(owner)) return;
    const popup = window.open("about:blank", "tribed-zoom");
    setBusy(true);
    setError("");
    try {
      const { url } = await startZoomAuth(communityId);
      if (popup) popup.location.assign(url);
      else window.location.assign(url);
    } catch (err: any) {
      if (popup) popup.close();
      setError(err?.message || "Could not start the Zoom connection.");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!communityId || !window.confirm("Disconnect this Zoom account?")) return;
    setBusy(true);
    setError("");
    try {
      await disconnectZoom(communityId);
      await refresh();
    } catch (err: any) {
      setError(err?.message || "Could not disconnect Zoom.");
    } finally {
      setBusy(false);
    }
  };

  const connected = Boolean(connection?.connected);
  const stale = Boolean(connection?.stale);
  const sharedFrom = connection?.sharedFrom || "";
  // A shared connection names the community it comes from, never the owner's
  // Zoom account: that account belongs to the other community.
  const account = sharedFrom
    ? `Hosting on ${sharedFrom}'s Zoom account`
    : connection?.displayName || connection?.email || "Connected";
  return (
    <Panel>
      <Copy>
        <Title>Zoom</Title>
        <Detail>
          {loading && !connection
            ? "Checking connection…"
            : connected
            ? `${account} · a unique Zoom meeting is created for every Zoom booking.`
            : "Connect the coach’s Zoom account to generate unique meeting links automatically."}
        </Detail>
      </Copy>
      {connected ? (
        <Tag>{stale ? "Reconnect required" : sharedFrom ? "Shared" : "Connected"}</Tag>
      ) : null}
      {connected && !stale ? (
        <Button variant="secondary" size="sm" onClick={disconnect} disabled={busy}>
          Disconnect
        </Button>
      ) : null}
      {/* A healthy alias shows both: the coach can take the grant over with
          their own Zoom account without stopping the sharing first. */}
      {!connected || stale || sharedFrom ? (
        <Button variant="primary" size="sm" onClick={connect} disabled={busy || !communityId}>
          {busy
            ? "Opening…"
            : sharedFrom
            ? "Connect own account"
            : stale
            ? "Reconnect Zoom"
            : "Connect Zoom"}
        </Button>
      ) : null}
      {connection?.staleReason ? <ErrorText>{connection.staleReason}</ErrorText> : null}
      {error ? <ErrorText>{error}</ErrorText> : null}
    </Panel>
  );
};

export default ZoomConnectPanel;
